import { useState, useEffect } from "react";
import {
  Input,
  Image,
  Text,
  FormControl,
  FormLabel,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalFooter,
  useDisclosure,
  FormErrorMessage,
  Box,
  Textarea,
  Checkbox,
  FormHelperText,
  useSafeLayoutEffect,
  Badge,
  Progress,
} from "@chakra-ui/react";
import { useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { talentOnboard } from "services";
import Error from "components/Error";
import DateInput from "components/DateInput";
import TalentSocialFormSection from "components/TalentSocialFormSection";
import { FaTimes, FaCheck } from "react-icons/fa";
import { uploadNewTalentPhotos } from "services/talent";
import { sizeDisplay, validateYear } from "utils";
import {
  MIN_FILE_SIZE,
  MAX_FILE_SIZE,
  MAX_PHOTO_SUBMISSION_COUNT,
} from "constants";
import useStyle from 'hooks/style.'
import heic2any from 'heic2any';
import {
  tokens,
  Select,
  Eyebrow,
  Panel,
  SectionRule,
  PrimaryButton,
  SecondaryButton,
  formFieldStyles,
} from './ui';

const ageGroupInstruction = [
  `Thank you for considering Paloma Model & Talent for representation. We are thrilled to be running our own agency and have the opportunity to provide children and their parents with dedicated, responsive, considerate, experienced, and protective assistance in the entertainment industry. Paloma Model & Talent is family owned and operated. We are hard-working sisters (just ask our husbands) with unparalleled drive, creativity, intelligence, industry knowledge, confidence, passion, spirit and gumption. But enough about us...

  Please submit recent outdoor pictures that are no more than a week old. If your child is older 6 months to 11 months, they must be sitting up in photos and if 12 months or older, standing. Please no photos with toys or food, and nothing in high chairs, strollers, or car seats. Keep hair natural with no hair accessories like bows, headbands, or hats. Photos must reflect how they look currently. We would like to see at least one smiling shot, a non-expression shot and a full-length body shot.

  You will get an automated response back once the submission has been successful. We will get back to you within two weeks by email so please add submissions@palomamodelandtalent.com and info@palomamodelandtalent.com to your address book to ensure the emails do not end up in spam. Thank you again for visiting us and taking this next step in making dreams come true for us all.`,

  `Thank you for considering Paloma Model & Talent for representation. We are grateful to have the opportunity to provide children and their parents with dedicated, responsive, considerate, experienced, and protective assistance in the entertainment industry.  We are hard-working sisters (just ask our husbands) and staff with unparalleled drive, creativity, intelligence, industry knowledge, confidence, passion, spirit and gumption. But enough about us…

  For our teen department, we are focused primarily on actors unless your teen still wears a kids size 12 or smaller, so please include links to their acting reels and samples. Please also upload a screenshot of their acting resume, or include their Actors Access or IMDb link in our links section. Feel free to add copious notes regarding their acting training and experience. Please submit recent pictures that reflect how they look now. We prefer to see current snapshots than older professional headshots, but please no shots in sunglasses or anything else that distracts from them. Please include a smiling shot showing teeth and a full-length shot, and crop photos so they fill the frame before uploading.

  You will get an automated email response once the submission has been successfully completed; please add submissions@palomamodelandtalent.com to your address book to ensure the email does not end up in spam. We will endeavor to get back to you within two weeks. Thank you again for visiting us and taking this next step in making dreams come true for us all.`,

  `You will get an automated response back once the submission has been successful.

  Currently, we are only accepting submissions from actors with established resumes and reel. Please include a link to these in your submission. Those seeking theatrical representation only must have co-star credits.
  
  We will get back to you within 30 days by email so please add submissions@palomamodelandtalent.com and info@palomamodelandtalent.com to your address book to ensure the emails do not end up in spam.`,
];

const defaultValues = {
  Babies: {
    address: {
      state: "CA",
    },
  },
  Adults: {
    is_over_25: null,
  },
};

const DetailForm = ({ ageGroup, back, postSubmit }) => {
  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
    watch,
    setValue,
    getValues,
  } = useForm({
    defaultValues: {
      talent: {
        ...defaultValues[ageGroup[1]],
        is_homeschooled: false,
        other_abled: false,
        is_over_25: null,
      },
    },
  });
  const { errorColor } = useStyle()
  const [apiError, setApiError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const enumData = useSelector((state) => state.talent.enumData);
  const { isOpen, onOpen: openConfirmModal, onClose } = useDisclosure();
  const [photos, setPhotos] = useState([]);

  const [failedPhotos, setFailedPhotos] = useState([]);
  const [successfulPhotos, setSuccessfulPhotos] = useState([]);

  // Terms consent is held outside react-hook-form on purpose. Both the outer
  // "Continue" form and this modal's "Submit" form share one useForm instance,
  // so registering this as a required field would make Continue fail validation
  // on a box that lives in a modal the user has not opened yet — Continue would
  // silently do nothing. Gating it here keeps the two steps independent.
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [termsError, setTermsError] = useState("");

  const closeConfirmModal = () => {
    // Consent does not survive backing out — reopening asks again.
    setAgreedToTerms(false);
    setTermsError("");
    onClose();
  };

  const submitData = async () => {
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      for (let i = 0; i < photos.length; i++) {
        if (
          photos[i].size < MAX_FILE_SIZE &&
          photos[i].size > MIN_FILE_SIZE && 
          !checkInSuccess(photos[i].name)
        ) {
          formData.append("files", photos[i]);
          console.log(photos[i])
        }
      };

      let responseInPhotoUpload;
      let allSuccessfulPhotos = [];

      if(formData)
        responseInPhotoUpload = await uploadNewTalentPhotos(formData);

      const { error, failedUploads, successfulUploads } = responseInPhotoUpload?.data;
      if (responseInPhotoUpload.status == 400) {
        setApiError(`Error! ${responseInPhotoUpload?.data?.error}`);
        setFailedPhotos((prevFailedPhotos) => [
          ...prevFailedPhotos,
          ...failedUploads.map((file) => ({ fileName: file.fileName, key: file.key })),
        ]);

        allSuccessfulPhotos = [
          ...successfulPhotos, 
          ...(successfulUploads?.map((file) => ({ fileName: file.fileName, key: file.key })) || [])
        ];
        setSuccessfulPhotos(allSuccessfulPhotos);

        onClose();
        setIsSubmitting(false);
        return;
      }
      if(successfulUploads){
        allSuccessfulPhotos = [
          ...successfulPhotos, 
          ...(successfulUploads?.map((file) => ({ fileName: file.fileName, key: file.key })) || [])
        ];
        setSuccessfulPhotos(allSuccessfulPhotos);
      }

      if (responseInPhotoUpload.status >= 500 && responseInPhotoUpload.status < 600) {
        setApiError('“Error! Your images have failed to upload. Please try again. If this error persists, please try a different browser or restarting your computer”');
        onClose();
        setIsSubmitting(false);
        return;
      }

      if (Array.isArray(responseInPhotoUpload.data) && responseInPhotoUpload.data.length === 0) {
        setApiError('“Error! Your images have failed to upload. Please try again. If this error persists, please try a different browser or restarting your computer”');
        onClose();
        setIsSubmitting(false);
        return;
      }
      const values = getValues();
      const keysArray = allSuccessfulPhotos.map(item => item.key);

      const res = await talentOnboard({...values.talent, photos: keysArray }, values.contact);
      postSubmit(res.data);
    } catch (err) {
      setApiError('“Error! Your images have failed to upload. Please try again. If this error persists, please try a different browser or restarting your computer”');
    }
    onClose();
    setIsSubmitting(false);
  };

  if (!enumData) {
    return;
  }

  const badPhotoCount = photos.filter(
    (photo) => photo.size > MAX_FILE_SIZE || photo.size < MIN_FILE_SIZE
  ).length;

  const isYoungAdult = ageGroup[0] === 2;

  // console.log("control: ", control);
  // console.log("errors: ", errors);

  const handleRetryUpload = () => {
    // Retry upload for failed photos
    const retryPhotos = failedPhotos.map((file) => new File([file.key], file.fileName));
    setPhotos(retryPhotos);
    setFailedPhotos([]);
  };

  const checkInFailed = (fileName) => {
    const failed = failedPhotos.find((f) => f.fileName === fileName);
    if(failed)
        return true;
    else
      return false;
  };


  const checkInSuccess = (fileName) => {
    const success = successfulPhotos.find((f) => f.fileName === fileName);
    if(success)
        return true;
    else
      return false;
  };

  return (
    <Box>
      <Panel
        eyebrow={`${ageGroup[1]} division`}
        title="Before you begin"
      >
        <Text
          whiteSpace="pre-line"
          fontSize="15px"
          lineHeight="1.7"
          color={tokens.text}
        >
          {ageGroupInstruction[ageGroup[0]]}
        </Text>
        {ageGroup[1] !== "Babies" && (
          <Text
            mt={6}
            px={5}
            py={4}
            bg={tokens.greenTint}
            borderLeftWidth="2px"
            borderColor={tokens.green}
            borderRadius="0 8px 8px 0"
            fontSize="15px"
            lineHeight="1.6"
            color={tokens.greenDark}
          >
            We currently cannot consider talent that lives outside the U.S.
          </Text>
        )}
      </Panel>
      <Panel
        eyebrow="Step two"
        title="Photos"
        containerProps={{ my: 7 }}
      >
        <Box display="flex" alignItems="end">
          <Input
            multiple
            type="file"
            id="new-photo-upload"
            name="files"
            accept="image/png, image/gif, image/jpeg, image/heic, image/heif, .heic"
            {...register("files", {
              // required: "Please choose at least one file",
              validate: (v) => photos.length > 0 ? true : "Please choose at least one file",
            })}
            disabled={isSubmitting}
            onChange={async (ev) => {
              const files = Array.from(ev.target.files);
              const imageFiles = await Promise.all(files.map(async (file) => {
                if (file.type === 'image/heic' || file.type === 'image/heif' || file.name.endsWith('.heic')) {
                  try {
                    const jpegBlob = await heic2any({
                      blob: file,
                      toType: 'image/jpeg',
                    });
                    return new File([jpegBlob], file.name.replace(/\.heic$/, '.jpg'));
                  } catch (error) {
                    console.error('HEIC conversion error:', error);
                    return null;
                  }
                } else {
                  return file;
                }
              }));
              const validImageFiles = imageFiles.filter((file) => file !== null);
              setPhotos(validImageFiles.concat(photos).slice(0, MAX_PHOTO_SUBMISSION_COUNT));

              // setPhotos(
              //   photos
              //     .concat(Array.from(ev.target.files))
              //     .slice(0, MAX_PHOTO_SUBMISSION_COUNT)
              // );
            }}
            style={{ display: "none" }}
          />
          <PrimaryButton
            disabled={isSubmitting}
            isLoading={isSubmitting}
            onClick={() => {
              document.querySelector("#new-photo-upload").click();
            }}
          >
            {photos.length > 0 ? "Add more photos" : "Upload photos"}
          </PrimaryButton>
          {photos.length > 0 && (
            <Eyebrow color={tokens.muted} ml={4} mb="14px">
              {photos.length} of {MAX_PHOTO_SUBMISSION_COUNT} selected
            </Eyebrow>
          )}
        </Box>
        <Box
          mt={5}
          px={5}
          py={4}
          bg="#fbfcfb"
          borderWidth="1px"
          borderColor={tokens.line2}
          borderRadius="10px"
        >
          <Eyebrow color={tokens.muted} mb="10px">Requirements</Eyebrow>
          <Box as="ul" pl={4} m={0} fontSize="14px" lineHeight="1.7" color={tokens.text}>
            <li>At least one photo, up to {MAX_PHOTO_SUBMISSION_COUNT}.</li>
            <li>
              Each file between {sizeDisplay(MIN_FILE_SIZE)} and{" "}
              {sizeDisplay(MAX_FILE_SIZE)}.
            </li>
            <li>JPG, PNG or HEIC only.</li>
          </Box>
        </Box>
        {errors.files && errors.files.message && (
          <Text mt={3} fontSize="14px" color={tokens.danger}>
            {errors.files.message}
          </Text>
        )}
        <Box
          mt={6}
          display="grid"
          gridTemplateColumns={{
            base: "1fr 1fr",
            md: "repeat(3, 1fr)",
            xl: "repeat(4, 1fr)",
          }}
          gridGap={4}
        >
          {photos.map((photo, idx) => (
            <Box
              key={`${idx}-${photo.name}`}
              position="relative"
              overflow="hidden"
              bg="#fbfcfb"
              borderRadius="12px"
              borderWidth="2px"
              h={60}
              borderColor={
                checkInFailed(photo.name) || photo.size > MAX_FILE_SIZE || photo.size < MIN_FILE_SIZE
                  ? tokens.danger :
                  checkInSuccess(photo.name) ? tokens.green
                  : tokens.line
              }
            >
              <Image
                opacity={
                  checkInFailed(photo.name) || photo.size > MAX_FILE_SIZE || photo.size < MIN_FILE_SIZE
                    ? 0.3
                    : 1
                }
                w="full"
                h="full"
                objectFit="contain"
                src={URL.createObjectURL(photo)}
              />
              <Box
                position="absolute"
                bottom={0}
                left={0}
                right={0}
                bgColor="rgba(255,255,255,.92)"
                px={3}
                py="6px"
              >
                <Text
                  fontSize="12px"
                  fontWeight="700"
                  letterSpacing="0.06em"
                  color={
                    photo.size > MAX_FILE_SIZE || photo.size < MIN_FILE_SIZE
                      ? tokens.danger
                      : tokens.muted
                  }
                >
                  {sizeDisplay(photo.size)}
                </Text>
              </Box>
              <Badge
                variant="solid"
                position="absolute"
                left={2}
                top={2}
                bg={checkInFailed(photo.name) ? tokens.danger : tokens.green}
                color="white"
                borderRadius="999px"
                px={2}
                py="2px"
                fontSize="11px"
                fontWeight="700"
                letterSpacing="0.08em"
                textTransform="uppercase"
              >
                {idx + 1}{checkInFailed(photo.name) ? ': Failed' : checkInSuccess(photo.name) ? ': Success' : ''}
              </Badge>
              <Box
                as="button"
                type="button"
                aria-label={`Remove photo ${idx + 1}`}
                position="absolute"
                right={2}
                top={2}
                display="flex"
                alignItems="center"
                justifyContent="center"
                w="26px"
                h="26px"
                borderRadius="999px"
                bg="rgba(255,255,255,.92)"
                color={tokens.ink}
                fontSize="11px"
                boxShadow="0 1px 3px rgba(17,24,21,.18)"
                _hover={{ bg: tokens.danger, color: 'white' }}
                onClick={() => {
                  const updatedPhotos = photos.slice(0);
                  updatedPhotos.splice(idx, 1);
                  setPhotos(updatedPhotos);
                }}
              >
                <FaTimes />
              </Box>
            </Box>
          ))}
        </Box>
        {badPhotoCount > 0 && (
          <Text mt={4} fontSize="14px" color={tokens.danger}>
            {badPhotoCount} photo{badPhotoCount === 1 ? " doesn't" : "s don't"} meet
            the size restriction.
          </Text>
        )}
      </Panel>
      <Panel eyebrow="Step three" title="Talent details">
        <Text fontSize="15px" lineHeight="1.7" color={tokens.muted} mb={5}>
          Please note we receive dozens of submissions every day. your patience
          with our review process is appreciated. We are constantly looking for
          new talent, but if you live outside of the Southern California area
          and are not a strong actor with credits and a reel, we are typically
          not able to offer representation at this time. Our adult department is
          focused on experienced actors at this time, not those looking solely
          for modeling work.
        </Text>
        <form onSubmit={handleSubmit(openConfirmModal)}>
          <Box
            sx={formFieldStyles}
            display="grid"
            gridTemplateColumns={{
              base: "1fr",
              lg: "1fr 1fr",
              xl: "repeat(4, 1fr)",
            }}
            mt={3}
            gridColumnGap={4}
            gridRowGap={5}
          >
            <SectionRule label="Talent" mt={0} pt={0} borderTopWidth="0" />
            <FormControl isInvalid={errors.talent && errors.talent.first_name}>
              <FormLabel>First Name *</FormLabel>
              <Input
                {...register("talent.first_name", {
                  required: "This field is required",
                })}
              />
              <FormErrorMessage>
                {errors.talent &&
                  errors.talent.first_name &&
                  errors.talent.first_name.message}
              </FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={errors.talent && errors.talent.last_name}>
              <FormLabel>Last Name *</FormLabel>
              <Input
                {...register("talent.last_name", {
                  required: "This field is required",
                })}
              />
              <FormErrorMessage>
                {errors.talent &&
                  errors.talent.last_name &&
                  errors.talent.last_name.message}
              </FormErrorMessage>
            </FormControl>
            {/* <FormControl>
              <FormLabel>Stage First Name</FormLabel>
              <Input {...register('talent.stage_first_name')}/>
            </FormControl>
            <FormControl>
              <FormLabel>Stage Last Name</FormLabel>
              <Input {...register('talent.stage_last_name')}/>
            </FormControl> */}

            <FormControl
              isInvalid={errors.talent && errors.talent.email}
              gridColumnStart="1"
            >
              <FormLabel>Email *</FormLabel>
              <Input
                type="email"
                {...register("talent.email", {
                  required: "This field is required",
                })}
              />
              <FormErrorMessage>
                {errors.talent &&
                  errors.talent.email &&
                  errors.talent.email.message}
              </FormErrorMessage>
            </FormControl>
            <FormControl
              isInvalid={errors.talent && errors.talent.confirm_email}
            >
              <FormLabel>Confirm Email *</FormLabel>
              <Input
                type="email"
                {...register("talent.confirm_email", {
                  required: "This field is required",
                  validate: (v) =>
                    watch("talent.email") === v
                      ? true
                      : "Confirm email doesn't match",
                })}
                onChange={(ev) => {
                  if (!isYoungAdult) {
                    setValue("contact.email", ev.target.value);
                  }
                }}
              />
              <FormErrorMessage>
                {errors.talent &&
                  errors.talent.confirm_email &&
                  errors.talent.confirm_email.message}
              </FormErrorMessage>
            </FormControl>
            <FormControl
              isInvalid={
                errors.talent && errors.talent.phones && errors.talent.phones[0]
              }
            >
              <FormLabel>Phone (1) *</FormLabel>
              <Input
                {...register("talent.phones[0]", {
                  required: "This field is required",
                })}
                onChange={(ev) => {
                  if (!isYoungAdult) {
                    setValue("contact.cell_number", ev.target.value);
                  }
                }}
              />
              <FormErrorMessage>
                {errors.talent &&
                  errors.talent.phones &&
                  errors.talent.phones[0] &&
                  errors.talent.phones[0].message}
              </FormErrorMessage>
            </FormControl>
            <FormControl>
              <FormLabel>Phone (2)</FormLabel>
              <Input {...register("talent.phones[1]")} />
            </FormControl>

            {/* <FormControl gridColumnStart='1'>
              <FormLabel>Street</FormLabel>
              <Input {...register('talent.address.street')} />
            </FormControl> */}
            <SectionRule label="Where they live" />
            <FormControl
              gridColumnStart="1"
              isInvalid={
                errors.talent &&
                errors.talent.address &&
                errors.talent.address.city
              }
            >
              <FormLabel>City *</FormLabel>
              <Input
                {...register("talent.address.city", {
                  required: "This field is required",
                })}
              />
              <FormErrorMessage>
                {errors.talent &&
                  errors.talent.address &&
                  errors.talent.address.city &&
                  errors.talent.address.city.message}
              </FormErrorMessage>
            </FormControl>
            <FormControl
              isInvalid={
                errors.talent &&
                errors.talent.address &&
                errors.talent.address.state
              }
            >
              <FormLabel>State *</FormLabel>
              <Input
                readOnly={ageGroup[1] === "Babies"}
                {...register("talent.address.state", {
                  required: "This field is required",
                })}
              />
              <FormErrorMessage>
                {errors.talent &&
                  errors.talent.address &&
                  errors.talent.address.state &&
                  errors.talent.address.state.message}
              </FormErrorMessage>
            </FormControl>
            <FormControl
              isInvalid={
                errors.talent &&
                errors.talent.address &&
                errors.talent.address.zip
              }
            >
              <FormLabel>Zip *</FormLabel>
              <Input
                {...register("talent.address.zip", {
                  required: "This field is required",
                })}
              />
              <FormErrorMessage>
                {errors.talent &&
                  errors.talent.address &&
                  errors.talent.address.zip &&
                  errors.talent.address.zip.message}
              </FormErrorMessage>
            </FormControl>

            <SectionRule label="Sizes &amp; appearance" />
            <FormControl
              isInvalid={errors.talent && errors.talent.cloth_size}
              gridColumnStart="1"
            >
              <FormLabel>Clothing Size *</FormLabel>
              <Select
                control={control}
                name="talent.cloth_size"
                rules={{ required: "This field is required" }}
                options={enumData.cloth_size}
              />
              <FormErrorMessage>
                {errors.talent &&
                  errors.talent.cloth_size &&
                  errors.talent.cloth_size.message}
              </FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={errors.talent && errors.talent.shoe_size}>
              <FormLabel>Shoe Size *</FormLabel>
              <Select
                control={control}
                name="talent.shoe_size"
                rules={{ required: "This field is required" }}
                options={enumData.shoe_size}
              />
              <FormErrorMessage>
                {errors.talent &&
                  errors.talent.shoe_size &&
                  errors.talent.shoe_size.message}
              </FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={errors.talent && errors.talent.height}>
              <FormLabel>Height *</FormLabel>
              <Select
                control={control}
                name="talent.height"
                rules={{ required: "This field is required" }}
                options={enumData.height}
              />
              <FormErrorMessage>
                {errors.talent &&
                  errors.talent.height &&
                  errors.talent.height.message}
              </FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={errors.talent && errors.talent.weight}>
              <FormLabel>Weight *</FormLabel>
              <Select
                control={control}
                name="talent.weight"
                rules={{ required: "This field is required" }}
                options={enumData.weight}
              />
              <FormErrorMessage>
                {errors.talent &&
                  errors.talent.weight &&
                  errors.talent.weight.message}
              </FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={errors.talent && errors.talent.eye_color}>
              <FormLabel>Eye Color *</FormLabel>
              <Select
                control={control}
                name="talent.eye_color"
                rules={{ required: "This field is required" }}
                options={enumData.eye_color}
              />
              <FormErrorMessage>
                {errors.talent &&
                  errors.talent.eye_color &&
                  errors.talent.eye_color.message}
              </FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={errors.talent && errors.talent.hair_color}>
              <FormLabel>Hair Color *</FormLabel>
              <Select
                control={control}
                name="talent.hair_color"
                rules={{ required: "This field is required" }}
                options={enumData.hair_color}
              />
              <FormErrorMessage>
                {errors.talent &&
                  errors.talent.hair_color &&
                  errors.talent.hair_color.message}
              </FormErrorMessage>
            </FormControl>

            <SectionRule label="About the talent" />
            <FormControl isInvalid={errors.talent && errors.talent.gender}>
              <FormLabel>Gender *</FormLabel>
              <Select
                control={control}
                isMulti
                name="talent.gender"
                rules={{ required: "This field is required" }}
                options={enumData.gender}
              />
              <FormErrorMessage>
                {errors.talent &&
                  errors.talent.gender &&
                  errors.talent.gender.message}
              </FormErrorMessage>
            </FormControl>
            <FormControl>
              <FormLabel>Identical Twin</FormLabel>
              <Select
                control={control}
                name="talent.is_twin"
                options={enumData.boolean_value}
              />
            </FormControl>
            {ageGroup[1] === "Adults" ? (
              <FormControl
                isInvalid={errors.talent && errors.talent.is_over_25}
              >
                <FormLabel>Is Over 25 ? *</FormLabel>
                <Select
                  control={control}
                  name="talent.is_over_25"
                  options={enumData.boolean_value}
                  rules={{
                    validate: (value, formValues) => value !== null
                    // required:
                    //   "You must select either over or under 25. We ask this to know if you are eligible to participate in alcohol advertising",
                  }}
                />
                <FormErrorMessage>
                  {errors.talent &&
                    errors.talent.is_over_25 &&
                    errors.talent.is_over_25.message}

                  {errors.talent &&
                    errors.talent.is_over_25 &&
                    errors.talent.is_over_25.type === 'validate' && "You must select either over or under 25. We ask this to know if you are eligible to participate in alcohol advertising"}
                </FormErrorMessage>
              </FormControl>
            ) : (
              <FormControl
                isInvalid={errors.talent && errors.talent.birth_date}
              >
                <FormLabel>Date of Birth *</FormLabel>
                <DateInput
                  control={control}
                  name="talent.birth_date"
                  rules={{ required: "This field is required", validate: validateYear }}
                />
                <FormErrorMessage>
                  {errors.talent &&
                    errors.talent.birth_date &&
                    errors.talent.birth_date.message}
                </FormErrorMessage>
              </FormControl>
            )}
            <FormControl>
              <FormLabel>Special Skills</FormLabel>
              <Select
                control={control}
                isMulti
                name="talent.special_skills"
                options={enumData.skills}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Other abled?</FormLabel>
              <Select
                control={control}
                name="talent.other_abled"
                options={enumData.boolean_value}
              />
            </FormControl>

            <SectionRule
              label="Heritage &amp; appearance"
              hint="We ask for both because casting briefs are written in terms of how someone reads on camera, which is often different from their actual background."
            />
            <FormControl
              gridColumn={{ base: "", lg: "1 / span 2" }}
              isInvalid={errors.talent && errors.talent.ethnicity_actual}
            >
              <FormLabel>Ethnic Heritage (actual) *</FormLabel>
              <Select
                control={control}
                isMulti
                name="talent.ethnicity_actual"
                rules={{ required: "This field is required" }}
                options={enumData.ethnicity}
              />
              <FormErrorMessage>
                {errors.talent &&
                  errors.talent.ethnicity_actual &&
                  errors.talent.ethnicity_actual.message}
              </FormErrorMessage>
            </FormControl>
            <FormControl
              gridColumn={{ base: "", lg: "span 2" }}
              isInvalid={errors.talent && errors.talent.ethnicity_look}
            >
              <FormLabel>Racial Appearance *</FormLabel>
              <Select
                control={control}
                isMulti
                name="talent.ethnicity_look"
                rules={{ required: "This field is required" }}
                options={enumData.racial_appearance}
              />
              <FormErrorMessage>
                {errors.talent &&
                  errors.talent.ethnicity_look &&
                  errors.talent.ethnicity_look.message}
              </FormErrorMessage>
            </FormControl>

            <FormControl gridColumn="1 / -1">
              <FormLabel>Actual Ethnic Heritage Notes</FormLabel>
              <Textarea
                minHeight="unset"
                rows={3}
                {...register("talent.ethnicity_note")}
              />
              <FormHelperText>
                Please specify associated country. We often get very specific
                requests (e.g. - Korean, Guatemalan, Filipino)
              </FormHelperText>
            </FormControl>

            <SectionRule label="Online presence &amp; notes" />
            <Box
              gridColumn="1 / -1"
              display="grid"
              gridTemplateColumns={{ base: "1fr", lg: "1fr 1fr" }}
              gridColumnGap={10}
              gridRowGap={8}
            >
              <Box>
                <Eyebrow color={tokens.muted} mb="10px">Social accounts</Eyebrow>
                <Text fontSize="14px" lineHeight="1.65" color={tokens.muted} mb={2}>
                  Please enter your full profile URL for any PUBLIC accounts.
                  Please verify all links before finalizing your submission. Do
                  not add any links to private accounts.
                </Text>
                <Text fontSize="14px" lineHeight="1.65" color={tokens.muted} mb={6}>
                  If you do not have any of these sites, please leave empty; do
                  not type N/A, etc. No need to add # of followers unless
                  significant.
                </Text>
                {/* TalentSocialFormSection stacks its FormControls with no
                    spacing of its own, and it is shared with the talent
                    dashboard — so the rhythm is applied from out here. */}
                <Box sx={{ '.chakra-form-control + .chakra-form-control': { mt: 5 } }}>
                  <TalentSocialFormSection
                    prefix="talent"
                    register={register}
                    setValue={setValue}
                  />
                </Box>
              </Box>
              <Box>
                {!["Babies", "Adults"].includes(ageGroup[1]) && (
                  <FormControl mb={8}>
                    <FormLabel>Is Homeschooled?</FormLabel>
                    <Select
                      control={control}
                      name="talent.is_homeschooled"
                      options={enumData.boolean_value}
                    />
                    <FormHelperText>
                      Does not include online schooling while enrolled in public
                      school.
                    </FormHelperText>
                  </FormControl>
                )}
                <Eyebrow color={tokens.muted} mb="10px">Other representation</Eyebrow>
                <Text fontSize="14px" lineHeight="1.65" color={tokens.muted} mb={4}>
                  Are you or your child currently represented by any other
                  agency or manager? If so, please list them and the respective
                  division (all, theatrical, commercial, etc).
                </Text>
                <FormControl mb={8}>
                  <FormLabel>Also represented by</FormLabel>
                  <Input {...register("talent.represented_by")} />
                </FormControl>

                <Eyebrow color={tokens.muted} mb="10px">Anything else</Eyebrow>
                <Text fontSize="14px" lineHeight="1.65" color={tokens.muted} mb={4}>
                  Please let us know your preferred pronouns and if you have any
                  other special skills, especially if you speak any other
                  languages. We love seeing video introductions so if you want
                  to send us one, please also include a link to your video in
                  the box below. This is your opportunity to tell us more about
                  you!
                </Text>
                <FormControl>
                  <FormLabel>Message</FormLabel>
                  <Textarea rows={6} {...register("talent.message")} />
                </FormControl>
              </Box>
            </Box>
            {!isYoungAdult && (
              <Box
                gridColumn="1 / -1"
                display="grid"
                // Was `window.innerWidth > 768 ? ... : ''`, which is read once at
                // render and never re-evaluates — the layout stayed wrong after a
                // resize or phone rotation. Breakpoints track the viewport live.
                gridTemplateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
                gridColumnGap={4}
                gridRowGap={5}
              >
                <SectionRule
                  label="Submitted by"
                  hint="The parent, guardian or manager completing this form."
                />
                <FormControl
                  gridColumnStart="1"
                  isInvalid={errors.contact && errors.contact.first_name}
                >
                  <FormLabel>First Name *</FormLabel>
                  <Input
                    {...register("contact.first_name", {
                      required: "This field is required",
                    })}
                  />
                  <FormErrorMessage>
                    {errors.contact &&
                      errors.contact.first_name &&
                      errors.contact.first_name.message}
                  </FormErrorMessage>
                </FormControl>
                <FormControl
                  isInvalid={errors.contact && errors.contact.last_name}
                >
                  <FormLabel>Last Name *</FormLabel>
                  <Input
                    {...register("contact.last_name", {
                      required: "This field is required",
                    })}
                  />
                  <FormErrorMessage>
                    {errors.contact &&
                      errors.contact.last_name &&
                      errors.contact.last_name.message}
                  </FormErrorMessage>
                </FormControl>
                <FormControl
                  isInvalid={errors.contact && errors.contact.relationship}
                >
                  <FormLabel>Relationship *</FormLabel>
                  <Input
                    {...register("contact.relationship", {
                      required: "This field is required",
                    })}
                  />
                  <FormErrorMessage>
                    {errors.contact &&
                      errors.contact.relationship &&
                      errors.contact.relationship.message}
                  </FormErrorMessage>
                </FormControl>
                <Input hidden type="email" {...register("contact.email")} />
                <Input hidden {...register("contact.cell_number")} />
                <Input
                  hidden
                  {...register("contact.contact_type")}
                  value={enumData.contact_type[0].value}
                />
              </Box>
            )}

            {/* <Error error={apiError} /> */}
            <Box
              gridColumn="1 / -1"
              mt={{ base: 8, md: 10 }}
              pt={6}
              borderTopWidth="1px"
              borderColor={tokens.line2}
            >
              <Error error={apiError} />
              {
                errors && Object.keys(errors).length > 0 &&
                <Text mt={2} mb={4} fontSize="14px" color={errorColor}>Please fix required fields above</Text>
              }
              {badPhotoCount > 0 && (
                <Text mt={2} mb={4} fontSize="14px" color={tokens.danger}>
                  {badPhotoCount} photo{badPhotoCount === 1 ? " doesn't" : "s don't"} meet
                  the size restriction.
                </Text>
              )}
              <Box display="flex" flexWrap="wrap" gap={3}>
                <PrimaryButton isDisabled={badPhotoCount > 0} type="submit">
                  Continue
                </PrimaryButton>
                <SecondaryButton onClick={back}>Back</SecondaryButton>
              </Box>
            </Box>
          </Box>
        </form>
      </Panel>

      <Modal
        isOpen={isOpen}
        onClose={closeConfirmModal}
        size="2xl"
        isCentered
        scrollBehavior="inside"
      >
        <ModalOverlay bg="rgba(17,24,21,.55)" />
        <ModalContent
          borderRadius="16px"
          overflow="hidden"
          color={tokens.text}
          mx={4}
        >
          <Box
            px={{ base: 6, md: 8 }}
            pt={7}
            pb={5}
            borderBottomWidth="1px"
            borderColor={tokens.line2}
          >
            <Eyebrow mb="10px">Last step</Eyebrow>
            <Text
              fontSize={{ base: '26px', md: '30px' }}
              lineHeight="1.15"
              color={tokens.ink}
            >
              Confirm and submit
            </Text>
          </Box>
          {/* Only the terms scroll. The checkbox and the Cancel/Submit buttons
              live in ModalFooter so they stay pinned and visible — with
              scrollBehavior="inside", anything outside ModalBody is fixed. */}
          <ModalBody px={{ base: 6, md: 8 }} py={{ base: 6, md: 7 }}>
            <Text fontSize="15px" lineHeight="1.7" color={tokens.text}>
              By checking this box you signify that you have answered these
              questions honestly and are submitting yourself or are the manager
              or legal guardian for the talent being submitted.
              <br />
              By submitting, you are confirming that the submitted talent has a
              valid U.S. Social Security number and can work legally in the
              United States, unless the talent is a newborn and a social is in
              process.
              <br />
              <br />
              Please add <strong>
                submissions@palomamodelandtalent.com
              </strong>{" "}
              to your address book or safe sender list to ensure email delivery.
              <br />
              If you do not receive a confirmation email then your submission
              was not processed; please check your spam or junk mail before
              re-submitting.
              <br />
              <br />
              We endeavor to get back to you within 3 weeks but it may be longer
              depending on our needs for talent in your demographic.
              <br />
              Click submit only once. The process may take a while depending on
              the size of your photos being uploaded.
            </Text>
          </ModalBody>
          <ModalFooter
            display="block"
            px={{ base: 6, md: 8 }}
            py={{ base: 5, md: 6 }}
            borderTopWidth="1px"
            borderColor={tokens.line2}
          >
            {isSubmitting && (
              <Box
                mb={5}
                px={5}
                py={4}
                bg={tokens.greenTint}
                borderRadius="10px"
              >
                <Text fontSize="14px" fontWeight="700" mb={3} color={tokens.greenDark}>
                  Uploading {photos.length} photo{photos.length !== 1 ? 's' : ''}... Please do not close this page.
                </Text>
                <Progress size="xs" isIndeterminate borderRadius="999px" sx={{
                  '& > div': { backgroundColor: tokens.green },
                }} />
              </Box>
            )}
            {/* noValidate hands the consent check to the gate below. Chakra's
                `isRequired` puts a native `required` on the checkbox, and the
                browser's own validation would otherwise fire first and show a
                transient native tooltip instead of the styled, persistent
                message the rest of this form uses. */}
            <form noValidate onSubmit={handleSubmit(() => {
              if (!agreedToTerms) {
                setTermsError("Please agree to the terms before submitting.");
                return;
              }
              submitData();
            })}>
              <Box
                px={5}
                py={4}
                mb={5}
                borderWidth="1px"
                borderColor={termsError ? tokens.danger : tokens.line}
                borderRadius="10px"
              >
                <FormControl isRequired isInvalid={!!termsError}>
                  <FormLabel display="flex" alignItems="center" m={0} fontSize="15px" color={tokens.ink}>
                    <Checkbox
                      mr={3}
                      colorScheme="green"
                      isChecked={agreedToTerms}
                      isDisabled={isSubmitting}
                      onChange={(ev) => {
                        setAgreedToTerms(ev.target.checked);
                        if (ev.target.checked) { setTermsError(""); }
                      }}
                    />
                    I agree to the terms
                  </FormLabel>
                  <FormErrorMessage fontSize="13px" color={tokens.danger} mt="8px">
                    {termsError}
                  </FormErrorMessage>
                </FormControl>
              </Box>

              <Box
                display="flex"
                flexWrap="wrap"
                justifyContent="flex-end"
                gap={3}
              >
                <SecondaryButton
                  onClick={closeConfirmModal}
                  disabled={isSubmitting}
                >
                  Cancel
                </SecondaryButton>
                <PrimaryButton
                  type="submit"
                  disabled={isSubmitting}
                  isLoading={isSubmitting}
                >
                  Submit
                </PrimaryButton>
              </Box>
            </form>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default DetailForm;
